/**
 * R-69 — Semantic action deduplication (intra-report + cross-report).
 * R-131-ROADMAP-FIX-A — structured deadline/owner extraction + suggestedPhase.
 * Clusters near-duplicate recommendations via embedding cosine similarity.
 */
import { Type } from '@google/genai';
import { cosineSimilarity } from '../embeddings/cosine';
import { embedText } from '../embeddings/embedText';
import type { SiteRagReport, Recommendation } from '../../types/siteRag';
import type { RecommendationPhase } from '../../types/recommendationGraph';
import { extractSections, isSerializedRecord, reportFullText } from './reportTextExtract';
import { AUDIT_CONFIG } from '../../config/auditConfig';
import { GEMINI_PRO_MODEL } from '../../config/geminiModels';

export interface ExtractedAction {
  id: string;
  text: string;
  sectionId: string;
  sourcePath: string;
  lineIndex?: number;
  owner?: string;
  deadline?: string;
  /** R-131-ROADMAP-FIX-A — heuristic phase when deadline is absent. */
  suggestedPhase?: RecommendationPhase;
  confidence?: number;
  citationRefs?: number[];
  /** Global ordering for canonical selection (lower = earlier). */
  order: number;
}

export interface ActionCluster {
  clusterId: string;
  canonical: ExtractedAction;
  members: ExtractedAction[];
  ownerConflict?: boolean;
  conflictingOwners?: string[];
  mergedCitationRefs?: number[];
  crossReport?: boolean;
}

export interface MarkdownPatch {
  sectionId: string;
  sourcePath: string;
  originalLines: string[];
  dedupedLines: string[];
  htmlComment: string;
}

export interface ActionDedupMeta {
  originalCount: number;
  dedupedCount: number;
  reductionRatio: number;
  threshold: number;
  applied?: boolean;
  crossReport?: boolean;
}

export interface ActionDedupResult {
  clusters: ActionCluster[];
  originalCount: number;
  dedupedCount: number;
  patches: MarkdownPatch[];
  applied: boolean;
  meta: ActionDedupMeta;
}

export type ActionEmbedder = (text: string) => Promise<number[]>;

const SECTION_TITLE_RE = /next\s*actions?|recommended\s*actions?|action\s*items?|recommendations?/i;
const BULLET_RE = /^\s*(?:[-*•]|\d+[.)])\s+(.+)$/;
const OWNER_RE = /\bowner\s*:\s*([^,;\n]+)/i;
const DEADLINE_RE = /\bdeadline\s*:\s*([^,;\n]+)/i;
const REF_RE = /\[Ref\s+(\d+)\]/gi;
const IMMEDIATE_SIGNAL_RE = /\b(immediate|asap|urgent|right away|now)\b/i;
const PHASE_BUCKET_TOKENS = ['immediate', '30d', '90d', '6mo', '12mo+'] as const;
const EXTRACTOR_MODEL = GEMINI_PRO_MODEL;

const SECTION_ORDER_RANK: Record<string, number> = {
  'executive-summary-actionPlan': 0,
  developmentPathway: 5,
  gridStudy: 10,
  transportStudy: 11,
  connectivityStudy: 12,
  powerAnalysis: 15,
  recommendations: 20,
  recommendedNextSteps: 25,
  'conclusion.nextActions': 30,
};

const EMBED_CACHE = new Map<string, { vector: number[]; cachedAt: number }>();

let orderCounter = 0;

function nextOrder(sectionId: string): number {
  const base = (SECTION_ORDER_RANK[sectionId] ?? 100) * 1000;
  orderCounter += 1;
  return base + orderCounter;
}

function hashText(text: string): string {
  const norm = text.trim().toLowerCase().replace(/\s+/g, ' ');
  let h = 5381;
  for (let i = 0; i < norm.length; i += 1) {
    h = ((h << 5) + h) ^ norm.charCodeAt(i);
  }
  return `r69-${(h >>> 0).toString(36)}`;
}

function parseCitationRefs(text: string): number[] {
  const refs = new Set<number>();
  for (const m of text.matchAll(REF_RE)) {
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n)) refs.add(n);
  }
  return [...refs].sort((a, b) => a - b);
}

function parseOwnerDeadline(text: string): { owner?: string; deadline?: string } {
  const owner = text.match(OWNER_RE)?.[1]?.trim();
  const deadline = text.match(DEADLINE_RE)?.[1]?.trim();
  return { owner: owner || undefined, deadline: deadline || undefined };
}

/** R-131-ROADMAP-FIX-A — parse explicit time signals into ISO or bucket tokens. */
export function parseTimeSignal(text: string, refDate = new Date()): string | null {
  const trimmed = text.trim();
  const lower = trimmed.toLowerCase();

  if (/^immediate(?:ly)?\s*:?\s*/i.test(trimmed) || IMMEDIATE_SIGNAL_RE.test(trimmed)) {
    return 'immediate';
  }

  for (const token of PHASE_BUCKET_TOKENS) {
    if (new RegExp(`\\b${token.replace('+', '\\+')}\\b`).test(lower)) return token;
  }

  const withinDays = lower.match(/\bwithin\s+(\d+)\s+days?\b/);
  if (withinDays) {
    const n = parseInt(withinDays[1], 10);
    if (n <= 7) return 'immediate';
    if (n <= 30) return '30d';
    if (n <= 90) return '90d';
    if (n <= 183) return '6mo';
    return '12mo+';
  }

  const withinMonths = lower.match(/\bwithin\s+(\d+)\s+months?\b/);
  if (withinMonths) {
    const n = parseInt(withinMonths[1], 10);
    if (n <= 1) return '30d';
    if (n <= 3) return '90d';
    if (n <= 6) return '6mo';
    return '12mo+';
  }

  const qMatch = lower.match(/\b(?:before|by|target|due)\s+q([1-4])\s+(\d{4})\b/);
  if (qMatch) {
    const q = parseInt(qMatch[1], 10);
    const year = parseInt(qMatch[2], 10);
    const endMonths = [3, 6, 9, 12];
    const endDays = [31, 30, 30, 31];
    const m = endMonths[q - 1];
    const d = endDays[q - 1];
    return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  const isoInText = trimmed.match(/\b(20\d{2}-\d{2}-\d{2})\b/);
  if (isoInText) return isoInText[1];

  const parsed = Date.parse(trimmed);
  if (Number.isFinite(parsed) && parsed > refDate.getTime() - 365 * 24 * 60 * 60 * 1000) {
    return new Date(parsed).toISOString().slice(0, 10);
  }

  return null;
}

/** R-131-ROADMAP-FIX-A — semantic phase suggestion when no explicit deadline. */
export function inferSuggestedPhase(text: string): RecommendationPhase | undefined {
  const t = text.toLowerCase();
  if (/\b(immediate|asap|urgent|expedite)\b/.test(t)) return 'immediate';
  if (/\b(due diligence|site assessment|desktop review|initial screening)\b/.test(t)) return '30d';
  if (/\b(permit application|permit filing|zoning application|rezone|variance|conditional use)\b/.test(t)) {
    return '90d';
  }
  if (/\b(feasibility study|interconnection study|environmental assessment|wetland delineation)\b/.test(t)) {
    return '90d';
  }
  if (/\b(construction|commission|commercial operation|cod|energization|long[\s-]?term|strategic)\b/.test(t)) {
    return '12mo+';
  }
  return undefined;
}

function normalizeDeadlineValue(raw: string | undefined, text: string, refDate: Date): string | undefined {
  const fromSignal = raw ? parseTimeSignal(raw, refDate) ?? raw.trim() : null;
  const fromText = parseTimeSignal(text, refDate);
  const deadline = fromSignal ?? fromText ?? raw?.trim();
  return deadline || undefined;
}

/** R-131-ROADMAP-FIX-A — regex + heuristic metadata when LLM is unavailable. */
export function resolveActionMetadataHeuristic(
  text: string,
  refDate = new Date(),
): Pick<ExtractedAction, 'owner' | 'deadline' | 'suggestedPhase'> {
  const { owner, deadline: regexDeadline } = parseOwnerDeadline(text);
  const deadline = normalizeDeadlineValue(regexDeadline, text, refDate);
  const suggestedPhase = deadline ? undefined : inferSuggestedPhase(text);
  return { owner, deadline, suggestedPhase };
}

export interface ActionMetadataEnrichment {
  owner?: string;
  deadline?: string;
  suggestedPhase?: RecommendationPhase;
  confidence?: number;
}

export type ActionMetadataExtractor = (
  actions: ExtractedAction[],
  refDate: Date,
) => Promise<ActionMetadataEnrichment[]>;

export interface ExtractActionsOptions {
  /** Skip Gemini enrichment (tests / offline); heuristics still apply. */
  skipLlmEnrichment?: boolean;
  metadataExtractor?: ActionMetadataExtractor;
  refDate?: Date;
}

function buildActionMetadataPrompt(actions: ExtractedAction[], refDate: Date): string {
  const refIso = refDate.toISOString().slice(0, 10);
  const lines = actions.map(
    (a, i) => `${i}. [${a.sectionId}] ${a.text}`,
  );
  return [
    'Extract structured metadata for each site-development recommendation below.',
    `Reference date: ${refIso}.`,
    '',
    'For each item return:',
    '- text: cleaned recommendation text (no owner/deadline prefixes)',
    '- owner: responsible party if stated or clearly implied, else null',
    '- deadline: ISO date (YYYY-MM-DD) OR bucket token (immediate, 30d, 90d, 6mo, 12mo+) when explicit time signals exist; else null',
    '- sourceSectionId: section id from the input line',
    '- confidence: 0-1 extraction confidence',
    '- suggestedPhase: when deadline is null, infer phase bucket from semantics (due diligence→30d, permit application→90d, construction→12mo+); else null',
    '',
    'Time signal examples:',
    '- "IMMEDIATELY: ..." → deadline "immediate"',
    '- "within 30 days" → deadline "30d"',
    '- "before Q3 2026" → deadline "2026-09-30"',
    '- "after Phase 1 zoning" → deadline null, suggestedPhase "90d" if permit/zoning implied',
    '',
    'Items:',
    ...lines,
  ].join('\n');
}

function parsePhaseToken(value: unknown): RecommendationPhase | undefined {
  if (typeof value !== 'string') return undefined;
  const v = value.trim().toLowerCase();
  if (v === 'unscheduled') return undefined;
  if ((PHASE_BUCKET_TOKENS as readonly string[]).includes(v)) return v as RecommendationPhase;
  return undefined;
}

function parseLlmActionMetadata(
  text: string | undefined | null,
  actionCount: number,
): ActionMetadataEnrichment[] {
  if (!text?.trim()) return [];
  try {
    const parsed = JSON.parse(text) as {
      actions?: Array<{
        text?: string;
        owner?: string | null;
        deadline?: string | null;
        sourceSectionId?: string;
        confidence?: number;
        suggestedPhase?: string | null;
      }>;
    };
    const rows = parsed.actions ?? [];
    return rows.slice(0, actionCount).map((row) => ({
      owner: typeof row.owner === 'string' && row.owner.trim() ? row.owner.trim() : undefined,
      deadline: typeof row.deadline === 'string' && row.deadline.trim()
        ? row.deadline.trim()
        : undefined,
      suggestedPhase: parsePhaseToken(row.suggestedPhase),
      confidence: typeof row.confidence === 'number'
        ? Math.max(0, Math.min(1, row.confidence))
        : undefined,
    }));
  } catch {
    return [];
  }
}

/** R-131-ROADMAP-FIX-A — structured Gemini Flash metadata extraction. */
export const defaultActionMetadataExtractor: ActionMetadataExtractor = async (actions, refDate) => {
  if (actions.length === 0) return [];
  try {
    const { ai } = await import('../../services/geminiClient');
    const response = await ai.models.generateContent({
      model: EXTRACTOR_MODEL,
      contents: buildActionMetadataPrompt(actions, refDate),
      config: {
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            actions: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  text: { type: Type.STRING },
                  owner: { type: Type.STRING, nullable: true },
                  deadline: { type: Type.STRING, nullable: true },
                  sourceSectionId: { type: Type.STRING },
                  confidence: { type: Type.NUMBER },
                  suggestedPhase: {
                    type: Type.STRING,
                    nullable: true,
                    enum: ['immediate', '30d', '90d', '6mo', '12mo+'],
                  },
                },
                required: ['text', 'sourceSectionId', 'confidence'],
              },
            },
          },
          required: ['actions'],
        },
      },
    });
    return parseLlmActionMetadata(response.text, actions.length);
  } catch {
    return [];
  }
};

export async function enrichActionsMetadata(
  actions: ExtractedAction[],
  opts: Pick<ExtractActionsOptions, 'skipLlmEnrichment' | 'metadataExtractor' | 'refDate'> = {},
): Promise<ExtractedAction[]> {
  if (actions.length === 0) return actions;
  const refDate = opts.refDate ?? new Date();
  let enrichments: ActionMetadataEnrichment[] = [];

  if (!opts.skipLlmEnrichment) {
    const extractor = opts.metadataExtractor ?? defaultActionMetadataExtractor;
    enrichments = await extractor(actions, refDate);
  }

  return actions.map((action, i) => {
    const llm = enrichments[i];
    const heuristic = resolveActionMetadataHeuristic(action.text, refDate);
    const deadline = llm?.deadline
      ? normalizeDeadlineValue(llm.deadline, action.text, refDate)
      : heuristic.deadline;
    const owner = llm?.owner ?? heuristic.owner;
    let suggestedPhase = deadline ? undefined : (llm?.suggestedPhase ?? heuristic.suggestedPhase);
    if (deadline && suggestedPhase) suggestedPhase = undefined;
    return {
      ...action,
      owner,
      deadline,
      suggestedPhase,
      confidence: llm?.confidence,
    };
  });
}

function normalizeActionText(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** Explicitly excluded paths are source caveats, not tasks to commission. */
function isExcludedAction(text: string): boolean {
  return /\[stance:\s*(?:not-considered|excluded|not-applicable)\b/i.test(text);
}

/** Development phases often contain descriptive model exhibits. Require an
 * action directive for these paragraphs rather than importing an entire
 * capacity/water explanation as one task. Structured recommendations keep
 * their existing admission contract. */
function isPathwayAction(text: string): boolean {
  return /(?:^|:\s*)(?:please\s+)?(?:obtain|commission|complete|verify|confirm|validate|execute|secure|request|review|resolve|prepare|submit|conduct|perform|develop|design|finali[sz]e|procure|negotiate|schedule|engage|initiate|assess|establish|implement|update|calculate|document|release|issue)\b/i.test(text);
}

function exactActionKey(text: string): string {
  return text.toLowerCase().replace(/\[ref\s+\d+\]/gi, ' ').replace(/\s+/g, ' ').trim().replace(/[.;,:]+$/g, '');
}

function makeActionId(sectionId: string, index: number): string {
  return `${sectionId}#${index}`;
}

function pushStringActions(
  out: ExtractedAction[],
  items: string[],
  sectionId: string,
  sourcePath: string,
): void {
  items.forEach((raw, i) => {
    const text = normalizeActionText(raw);
    if (!text || isExcludedAction(text)) return;
    out.push({
      id: makeActionId(sectionId, out.length),
      text,
      sectionId,
      sourcePath,
      lineIndex: i,
      citationRefs: parseCitationRefs(text),
      order: nextOrder(sectionId),
    });
  });
}

function pushRecommendationObjects(
  out: ExtractedAction[],
  recs: Recommendation[] | undefined,
  sectionId: string,
  sourcePath: string,
): void {
  for (const rec of recs ?? []) {
    const parts = [rec.title, rec.description].filter((s) => typeof s === 'string' && s.trim());
    const text = normalizeActionText(parts.join(': '));
    if (!text || isExcludedAction(text)) continue;
    out.push({
      id: makeActionId(sectionId, out.length),
      text,
      sectionId,
      sourcePath,
      citationRefs: parseCitationRefs(text),
      order: nextOrder(sectionId),
    });
  }
}

function extractMarkdownSectionActions(md: string): ExtractedAction[] {
  const out: ExtractedAction[] = [];
  const lines = md.split('\n');
  let inSection = false;
  let currentSectionId = 'markdown:actions';
  let lineIndex = 0;

  for (const line of lines) {
    const heading = line.match(/^#{1,4}\s+(.+)$/);
    if (heading) {
      const title = heading[1].trim();
      inSection = SECTION_TITLE_RE.test(title);
      if (inSection) {
        currentSectionId = `markdown:${title.toLowerCase().replace(/\s+/g, '-')}`;
        lineIndex = 0;
      }
      continue;
    }
    if (!inSection) continue;

    const bullet = line.match(BULLET_RE);
    if (bullet) {
      const text = normalizeActionText(bullet[1]);
      if (!text || isExcludedAction(text)) continue;
      out.push({
        id: makeActionId(currentSectionId, out.length),
        text,
        sectionId: currentSectionId,
        sourcePath: `markdown:${currentSectionId}`,
        lineIndex: lineIndex++,
        citationRefs: parseCitationRefs(text),
        order: nextOrder(currentSectionId),
      });
      continue;
    }

    const trimmed = line.trim();
    if (trimmed.length > 20 && !/^#{1,6}\s/.test(trimmed) && !isExcludedAction(trimmed) && isPathwayAction(trimmed)) {
      const text = normalizeActionText(trimmed);
      out.push({
        id: makeActionId(currentSectionId, out.length),
        text,
        sectionId: currentSectionId,
        sourcePath: `markdown:${currentSectionId}`,
        lineIndex: lineIndex++,
        citationRefs: parseCitationRefs(text),
        order: nextOrder(currentSectionId),
      });
    }
  }
  return out;
}

/** Collect raw actionable items from structured fields and markdown action sections. */
function collectRawActions(report: SiteRagReport): ExtractedAction[] {
  orderCounter = 0;
  const out: ExtractedAction[] = [];

  pushRecommendationObjects(out, report.recommendations, 'recommendations', 'recommendations');
  pushStringActions(out, report.conclusion?.nextActions ?? [], 'conclusion.nextActions', 'conclusion.nextActions');

  const pathway = report.developmentPathway ?? [];
  pushStringActions(
    out,
    pathway.map((p) => normalizeActionText([p.title, p.description].filter(Boolean).join(': ')))
      .filter(isPathwayAction),
    'developmentPathway',
    'developmentPathway',
  );

  pushStringActions(out, report.gridStudy?.recommendations ?? [], 'gridStudy', 'gridStudy.recommendations');
  pushStringActions(out, report.transportStudy?.recommendations ?? [], 'transportStudy', 'transportStudy.recommendations');
  pushStringActions(out, report.connectivityStudy?.recommendations ?? [], 'connectivityStudy', 'connectivityStudy.recommendations');
  pushStringActions(out, report.powerAnalysis?.recommendations ?? [], 'powerAnalysis', 'powerAnalysis.recommendations');

  const r = report as SiteRagReport & { recommendedNextSteps?: string[] };
  if (Array.isArray(r.recommendedNextSteps)) {
    pushStringActions(out, r.recommendedNextSteps, 'recommendedNextSteps', 'recommendedNextSteps');
  }

  for (const section of extractSections(report)) {
    if (!SECTION_TITLE_RE.test(section.id)) continue;
    const lines = section.content.split('\n');
    const bullets: string[] = [];
    for (const line of lines) {
      const m = line.match(BULLET_RE);
      // Structured recommendations were already read above. Their generated
      // "Type: … | Title: … | Description: …" representation is audit text,
      // not a second action for the roadmap.
      if (m && !isSerializedRecord(line)) bullets.push(m[1]);
    }
    if (bullets.length > 0) {
      pushStringActions(out, bullets, section.id, section.id);
    }
  }

  const md = (report as SiteRagReport & { markdown?: string }).markdown;
  if (typeof md === 'string' && md.trim()) {
    out.push(...extractMarkdownSectionActions(md));
  }

  return out;
}

/** Bind exactly the advice consumed by extractActions, including legacy
 * structured recommendations beside markdown. This performs no enrichment
 * or embedding request and excludes QA/delivery metadata. */
export function roadmapActionInputIdentity(report: SiteRagReport): string {
  return JSON.stringify(collectRawActions(report));
}

/** R-131-ROADMAP-FIX-A — extract actions with structured deadline/owner metadata. */
export async function extractActions(
  report: SiteRagReport,
  opts: ExtractActionsOptions = {},
): Promise<ExtractedAction[]> {
  const raw = collectRawActions(report);
  return enrichActionsMetadata(raw, opts);
}

/** Synthetic actions from executive-summary gemini actionPlan strings. */
export async function extractExecutiveSummaryActionPlan(
  actionPlan: string[],
  opts: ExtractActionsOptions = {},
): Promise<ExtractedAction[]> {
  orderCounter = 0;
  const out: ExtractedAction[] = [];
  pushStringActions(out, actionPlan, 'executive-summary-actionPlan', 'executive-summary-actionPlan');
  return enrichActionsMetadata(out, opts);
}

function selectCanonical(members: ExtractedAction[]): ExtractedAction {
  const sorted = [...members].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return b.text.length - a.text.length;
  });
  return sorted[0];
}

async function embedWithCache(text: string, embedder: ActionEmbedder): Promise<number[]> {
  const key = hashText(text);
  const ttl = AUDIT_CONFIG.ACTION_EMBED_CACHE_TTL_MS;
  const hit = EMBED_CACHE.get(key);
  if (hit && Date.now() - hit.cachedAt < ttl) return hit.vector;
  const vector = await embedder(text);
  EMBED_CACHE.set(key, { vector, cachedAt: Date.now() });
  return vector;
}

/** Greedy clustering: join cluster when cosine ≥ threshold vs any member. */
export async function clusterActions(
  actions: ExtractedAction[],
  embedder: ActionEmbedder,
  threshold: number,
  opts: { crossReport?: boolean } = {},
): Promise<ActionCluster[]> {
  if (actions.length === 0) return [];

  const vectors = await Promise.all(actions.map((a) => embedWithCache(a.text, embedder)));
  const clusters: ActionCluster[] = [];

  for (let i = 0; i < actions.length; i += 1) {
    const action = actions[i];
    const vec = vectors[i];
    let assigned = false;

    for (const cluster of clusters) {
      // Exact duplicates must merge even when embedding is empty/unavailable.
      // Citation suffixes retain their separate source references below.
      if (exactActionKey(action.text) === exactActionKey(cluster.canonical.text)) {
        cluster.members.push(action);
        cluster.canonical = selectCanonical(cluster.members);
        assigned = true;
        break;
      }
      for (const member of cluster.members) {
        const memberIdx = actions.findIndex((a) => a.id === member.id);
        if (memberIdx < 0) continue;
        const sim = cosineSimilarity(vec, vectors[memberIdx]);
        if (sim >= threshold) {
          cluster.members.push(action);
          cluster.canonical = selectCanonical(cluster.members);
          assigned = true;
          break;
        }
      }
      if (assigned) break;
    }

    if (!assigned) {
      clusters.push({
        clusterId: `cluster-${clusters.length}`,
        canonical: action,
        members: [action],
        crossReport: opts.crossReport,
      });
    }
  }

  for (const cluster of clusters) {
    cluster.canonical = selectCanonical(cluster.members);
    const owners = new Set(
      cluster.members.map((m) => m.owner?.trim().toLowerCase()).filter((o): o is string => Boolean(o)),
    );
    if (owners.size > 1) {
      cluster.ownerConflict = true;
      cluster.conflictingOwners = [...owners];
    }
    const refs = new Set<number>();
    for (const m of cluster.members) {
      for (const r of m.citationRefs ?? []) refs.add(r);
    }
    cluster.mergedCitationRefs = [...refs].sort((a, b) => a - b);
  }

  return clusters;
}

function formatCitationSuffix(refs: number[] | undefined): string {
  if (!refs?.length) return '';
  return ` ${refs.map((n) => `[Ref ${n}]`).join(' ')}`;
}

function buildHtmlComment(cluster: ActionCluster): string {
  const lines = cluster.members.map(
    (m) => `- ${m.text} (${m.sectionId} / ${m.sourcePath})`,
  );
  return [
    `<!-- r69-action-dedup clusterId=${cluster.clusterId}`,
    'originals:',
    ...lines,
    '-->',
  ].join('\n');
}

/** Build markdown/section patches for reversible dedup (HTML comment preserves originals). */
export async function buildDedupPatch(
  report: SiteRagReport,
  clusters: ActionCluster[],
  opts: ExtractActionsOptions = {},
): Promise<MarkdownPatch[]> {
  const actions = await extractActions(report, opts);
  const actionToCluster = new Map<string, ActionCluster>();
  for (const c of clusters) {
    for (const m of c.members) actionToCluster.set(m.id, c);
  }

  const bySource = new Map<string, ExtractedAction[]>();
  for (const a of actions) {
    const list = bySource.get(a.sourcePath) ?? [];
    list.push(a);
    bySource.set(a.sourcePath, list);
  }

  const patches: MarkdownPatch[] = [];

  for (const [sourcePath, sourceActions] of bySource) {
    const originalLines = sourceActions.map((a) => a.text);
    const seenClusters = new Set<string>();
    const dedupedLines: string[] = [];
    const relatedClusters = clusters.filter((c) =>
      c.members.some((m) => m.sourcePath === sourcePath),
    );

    for (const a of sourceActions) {
      const cluster = actionToCluster.get(a.id);
      if (!cluster) {
        dedupedLines.push(a.text);
        continue;
      }
      if (cluster.canonical.id !== a.id) continue;
      if (cluster.canonical.sourcePath !== sourcePath) continue;
      if (seenClusters.has(cluster.clusterId)) continue;
      seenClusters.add(cluster.clusterId);
      let line = cluster.canonical.text;
      const suffix = formatCitationSuffix(cluster.mergedCitationRefs);
      if (suffix && !/\[Ref\s+\d+\]/i.test(line)) line += suffix;
      dedupedLines.push(line);
    }

    const removedCount = sourceActions.filter((a) => {
      const cluster = actionToCluster.get(a.id);
      return cluster && cluster.canonical.id !== a.id;
    }).length;
    if (dedupedLines.length === originalLines.length && removedCount === 0) continue;

    const htmlComment = relatedClusters.map(buildHtmlComment).join('\n');
    patches.push({
      sectionId: sourceActions[0]?.sectionId ?? sourcePath,
      sourcePath,
      originalLines,
      dedupedLines,
      htmlComment,
    });
  }

  return patches;
}

function applyPatchesToReport(report: SiteRagReport, patches: MarkdownPatch[]): SiteRagReport {
  let out: SiteRagReport = { ...report };

  for (const patch of patches) {
    if (patch.sourcePath === 'recommendations') {
      out = {
        ...out,
        recommendations: patch.dedupedLines.map((text, i) => {
          const existing = out.recommendations?.[i];
          if (existing?.title) {
            return { ...existing, title: text.split(':')[0]?.trim() || text, description: text.includes(':') ? text.split(':').slice(1).join(':').trim() : existing.description };
          }
          return { type: 'site' as const, title: text, description: '', rationale: '' };
        }),
      };
      continue;
    }
    if (patch.sourcePath === 'conclusion.nextActions' && out.conclusion) {
      out = {
        ...out,
        conclusion: { ...out.conclusion, nextActions: patch.dedupedLines },
      };
      continue;
    }
    if (patch.sourcePath === 'developmentPathway') {
      out = {
        ...out,
        developmentPathway: patch.dedupedLines.map((text, i) => {
          const prev = out.developmentPathway?.[i];
          const [title, ...rest] = text.split(':');
          return {
            phase: prev?.phase ?? `Phase ${i + 1}`,
            title: title?.trim() || text,
            description: rest.join(':').trim() || prev?.description || '',
          };
        }),
      };
      continue;
    }
    if (patch.sourcePath === 'gridStudy.recommendations' && out.gridStudy) {
      out = { ...out, gridStudy: { ...out.gridStudy, recommendations: patch.dedupedLines } };
      continue;
    }
    if (patch.sourcePath === 'transportStudy.recommendations' && out.transportStudy) {
      out = { ...out, transportStudy: { ...out.transportStudy, recommendations: patch.dedupedLines } };
      continue;
    }
    if (patch.sourcePath === 'connectivityStudy.recommendations' && out.connectivityStudy) {
      out = { ...out, connectivityStudy: { ...out.connectivityStudy, recommendations: patch.dedupedLines } };
      continue;
    }
    if (patch.sourcePath === 'powerAnalysis.recommendations' && out.powerAnalysis) {
      out = { ...out, powerAnalysis: { ...out.powerAnalysis, recommendations: patch.dedupedLines } };
      continue;
    }
    if (patch.sourcePath === 'recommendedNextSteps') {
      (out as SiteRagReport & { recommendedNextSteps?: string[] }).recommendedNextSteps = patch.dedupedLines;
      continue;
    }
    if (patch.sourcePath.startsWith('markdown:')) {
      const md = (out as SiteRagReport & { markdown?: string }).markdown ?? '';
      if (!md) continue;
      let updated = md;
      for (const orig of patch.originalLines) {
        if (patch.dedupedLines.includes(orig)) continue;
        const escaped = orig.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        updated = updated.replace(new RegExp(`^\\s*(?:[-*•]|\\d+[.)])\\s+${escaped}\\s*$`, 'm'), '');
      }
      const insertBlock = `${patch.htmlComment}\n${patch.dedupedLines.map((l) => `- ${l}`).join('\n')}\n`;
      if (!updated.includes(patch.htmlComment)) {
        updated = `${updated.trim()}\n\n${insertBlock}`;
      }
      (out as SiteRagReport & { markdown?: string }).markdown = updated.replace(/\n{3,}/g, '\n\n');
    }
  }

  return out;
}

export async function dedupReportActions(
  report: SiteRagReport,
  embedder: ActionEmbedder,
  opts: {
    threshold?: number;
    autoDedup?: boolean;
    minReductionRatio?: number;
  } & ExtractActionsOptions = {},
): Promise<ActionDedupResult> {
  const threshold = opts.threshold ?? AUDIT_CONFIG.ACTION_DEDUP_THRESHOLD;
  const actions = await extractActions(report, opts);
  const clusters = await clusterActions(actions, embedder, threshold);
  const patches = await buildDedupPatch(report, clusters, opts);
  const originalCount = actions.length;
  const dedupedCount = clusters.length;
  const reductionRatio = originalCount > 0 ? (originalCount - dedupedCount) / originalCount : 0;
  const shouldApply = Boolean(opts.autoDedup)
    && dedupedCount < originalCount
    && reductionRatio >= (opts.minReductionRatio ?? 0.15);

  return {
    clusters,
    originalCount,
    dedupedCount,
    patches,
    applied: shouldApply,
    meta: {
      originalCount,
      dedupedCount,
      reductionRatio,
      threshold,
      applied: shouldApply,
    },
  };
}

const DEDUP_FOOTNOTE = 'actions deduplicated — see Audit tab';

/** Cross-report clustering for executive-summary actionPlan + source reports. */
export async function clusterCrossReportActions(
  sourceReports: SiteRagReport[],
  executiveActionPlan: string[],
  embedder: ActionEmbedder,
  opts: ExtractActionsOptions = {},
): Promise<{ clusters: ActionCluster[]; canonicalTexts: string[] }> {
  const all: ExtractedAction[] = [];
  for (const r of sourceReports) {
    all.push(...await extractActions(r, opts));
  }
  all.push(...await extractExecutiveSummaryActionPlan(executiveActionPlan, opts));
  const threshold = AUDIT_CONFIG.ACTION_DEDUP_CROSS_REPORT_THRESHOLD;
  const clusters = await clusterActions(all, embedder, threshold, { crossReport: true });
  const canonicalTexts = clusters.map((c) => {
    let text = c.canonical.text;
    const suffix = formatCitationSuffix(c.mergedCitationRefs);
    if (suffix && !/\[Ref\s+\d+\]/i.test(text)) text += suffix;
    return text;
  });
  return { clusters, canonicalTexts };
}

export async function applyActionDedupGate(
  report: SiteRagReport,
  opts: {
    autoDedup?: boolean;
    minReductionRatio?: number;
    embedder?: ActionEmbedder;
  } & Pick<ExtractActionsOptions, 'skipLlmEnrichment'> = {},
): Promise<SiteRagReport> {
  const embedder = opts.embedder ?? ((text: string) => embedText(text, { taskType: 'SEMANTIC_SIMILARITY' }));
  const result = await dedupReportActions(report, embedder, {
    autoDedup: opts.autoDedup ?? AUDIT_CONFIG.AUTO_DEDUP_ACTIONS,
    minReductionRatio: opts.minReductionRatio ?? 0.15,
    skipLlmEnrichment: opts.skipLlmEnrichment,
  });

  let out = report;
  if (result.applied) {
    out = applyPatchesToReport(report, result.patches);
    const removed = result.originalCount - result.dedupedCount;
    const footnote = `\n\n_(${removed} ${DEDUP_FOOTNOTE})_`;
    const md = (out as SiteRagReport & { markdown?: string }).markdown;
    if (typeof md === 'string' && !md.includes(DEDUP_FOOTNOTE)) {
      (out as SiteRagReport & { markdown?: string }).markdown = md + footnote;
    } else if (!md) {
      (out as SiteRagReport & { summary?: string }).summary = `${out.summary ?? ''}${footnote}`.trim();
    }
  }

  const clusterPayload = result.clusters.map((c) => ({
    clusterId: c.clusterId,
    canonicalText: c.canonical.text,
    canonicalSectionId: c.canonical.sectionId,
    memberCount: c.members.length,
    members: c.members.map((m) => ({
      text: m.text,
      sectionId: m.sectionId,
      sourcePath: m.sourcePath,
      owner: m.owner,
      deadline: m.deadline,
      suggestedPhase: m.suggestedPhase,
      citationRefs: m.citationRefs,
    })),
    ownerConflict: c.ownerConflict,
    conflictingOwners: c.conflictingOwners,
    mergedCitationRefs: c.mergedCitationRefs,
    crossReport: c.crossReport,
    htmlComment: buildHtmlComment(c),
  }));

  const priorCross = (report.qaFindings?.actionClusters ?? []).filter((c) => c.crossReport);
  const intraClusters = clusterPayload.filter((c) => !c.crossReport);
  const mergedClusters = priorCross.length > 0
    ? [...priorCross, ...intraClusters]
    : clusterPayload;
  const meta = priorCross.length > 0 && result.meta.crossReport !== true
    ? { ...result.meta, crossReport: true }
    : result.meta;

  return {
    ...out,
    qaFindings: {
      ...(out.qaFindings ?? {}),
      actionClusters: mergedClusters,
      actionDedupMeta: meta,
    },
  };
}

/** Clear embed cache (tests). */
export function clearActionEmbedCache(): void {
  EMBED_CACHE.clear();
}

export { reportFullText };
